import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type LatelyImageDb = {
  userId: string;
  imageIds: string[];
}
const initial: LatelyImageDb = {
  userId: '',
  imageIds: []
};

export type LatelyImageDbHashType = {
  [key in string]: LatelyImageDb;
}

export type LatelyImageAddPayload = {
  id?: string;
} & LatelyImageDb;
export type LatelyImageUpdatePayload = {
  id: string;
} & { [P in keyof LatelyImageDb]?: LatelyImageDb[P] };

export type LatelyImageDbState = {
  latelyImages: LatelyImageDbHashType;
}

export const generateLatelyImageId = (): string => generateId('li');
let initialState: LatelyImageDbState = { latelyImages: {} };
const key = 'latelyImages';

const latelyImageDbSlice = createSlice({
  name: 'latelyImageDb',
  initialState,
  reducers: {
    initializeLatelyImages(state: LatelyImageDbState) {
      state.latelyImages = {};
    },
    addLatelyImage(state: LatelyImageDbState, action: PayloadAction<LatelyImageAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateLatelyImageId();
      state.latelyImages[id] = obj;
      saveData(key, id, obj);
    },
    updateLatelyImage(state: LatelyImageDbState, action: PayloadAction<LatelyImageUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.latelyImages[id] === undefined) {
        console.log(`*** LatelyImages not found: ${id}`);
      } else {
        const newData = { ...state.latelyImages[id], ...obj };
        state.latelyImages[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteLatelyImage(state: LatelyImageDbState, action: PayloadAction<LatelyImageUpdatePayload>) {
      const id = action.payload.id;
      delete state.latelyImages[id];
      deleteData(key, id);
    },
    saveLatelyImages(state: LatelyImageDbState) {
      saveAllData(key, state.latelyImages);
    },
    signUpLatelyImages(state: LatelyImageDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.latelyImages, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: LatelyImageDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.latelyImages = updatedValues;
      if (changed) saveAllData(key, state.latelyImages);
    })
  }
})

export const { initializeLatelyImages, addLatelyImage, updateLatelyImage, deleteLatelyImage, saveLatelyImages, signUpLatelyImages } = latelyImageDbSlice.actions;
export default latelyImageDbSlice.reducer;
